/**
 * Ticker Polling Service - Fallback mechanism for ticker price updates
 * Polls the database for ticker updates when WebSocket notifications might be missed
 */

import { getAggregatedTickers } from './api';
import { createLogger } from '../utils/debugLogger';

const POLLING_INTERVAL_STORAGE_KEY = 'tickerPollingIntervalMs';

// Create debug logger for this module
const logger = createLogger('tickerPolling', 'Background ticker polling mechanism', false, 'DEBUG');

class TickerPollingService {
  constructor() {
    this.pollingTimer = null;
    this.isPolling = false;
    this.lastPollTime = null;
    this.lastKnownUpdateTime = null;
    this.defaultPollingIntervalMs = 60000; // 1 minute default
    this.pollingIntervalMs = this.defaultPollingIntervalMs;
    this.consecutiveEmptyPolls = 0;
    this.subscribers = new Set();
    this.isEnabled = true;
    this.storageKey = POLLING_INTERVAL_STORAGE_KEY;
    this.marketClosed = false;

    const storedInterval = this.loadStoredInterval();
    if (storedInterval) {
      this.pollingIntervalMs = storedInterval;
    }

    // Bind methods to preserve context
    this.startPolling = this.startPolling.bind(this);
    this.stopPolling = this.stopPolling.bind(this);
    this.poll = this.poll.bind(this);
  }

  /**
   * Subscribe to polling updates
   * @param {Function} callback - Function to call when ticker updates are found
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Notify all subscribers of ticker updates
   * @param {Object} data - Ticker update data
   */
  notifySubscribers(data) {
    this.subscribers.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        logger.error('Error in ticker polling subscriber:', error);
      }
    });
  }

  /**
   * Start polling for ticker updates
   * @param {number} intervalMs - Polling interval in milliseconds
   */
  startPolling(intervalMs = this.pollingIntervalMs) {
    const nextInterval = intervalMs || this.defaultPollingIntervalMs;

    if (this.isPolling) {
      if (this.pollingIntervalMs === nextInterval) {
        logger.info('Ticker polling already active');
        return;
      }

      logger.info('Restarting ticker polling with new interval');
      this.stopPolling();
    }

    this.pollingIntervalMs = nextInterval;
    this.saveStoredInterval(nextInterval);

    logger.info(`Starting ticker polling every ${this.pollingIntervalMs}ms`);
    this.isPolling = true;
    this.consecutiveEmptyPolls = 0;

    // Start polling immediately, then at intervals
    this.poll();
    this.pollingTimer = setInterval(this.poll, this.pollingIntervalMs);
  }

  /**
   * Stop polling for ticker updates
   */
  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.isPolling = false;
    logger.info('Ticker polling stopped');
  }

  /**
   * Update the polling interval used by the service
   * @param {number} intervalMs - Desired polling interval in milliseconds
   */
  setPollingInterval(intervalMs) {
    const parsed = Number(intervalMs);
    const normalized = Number.isFinite(parsed) && parsed > 0
      ? Math.max(1000, Math.floor(parsed))
      : this.defaultPollingIntervalMs;

    if (this.pollingIntervalMs === normalized) {
      return;
    }

    logger.info(`Updating ticker polling interval to ${normalized}ms`);
    this.pollingIntervalMs = normalized;
    this.saveStoredInterval(normalized);

    if (this.isPolling) {
      this.stopPolling();
      this.startPolling(normalized);
    }
  }

  /**
   * Perform a single poll for ticker updates
   */
  async poll() {
    if (!this.isEnabled) {
      return;
    }

    const pollTimestamp = new Date();

    try {
      logger.debug('Polling for ticker updates...');
      const response = await getAggregatedTickers();

      if (response && response.data) {
        const responseData = response.data;
        const tickers = responseData.tickers || [];
        const summary = responseData.summary || null;
        const marketClosed = responseData.market_closed;
        const marketInfo = responseData.market_info;
        const responseUpdateTime = responseData.latest_update_time
          ? new Date(responseData.latest_update_time)
          : null;

        // Update market closed status
        if (marketClosed !== undefined) {
          this.marketClosed = marketClosed;
          if (marketClosed) {
            logger.info('Market is closed - continuing polling while marking market status');
          }
        }

        const currentUpdateTime = this.getLatestUpdateTime(tickers);
        const effectiveUpdateTime = currentUpdateTime || responseUpdateTime || this.lastKnownUpdateTime;
        const hasTickerUpdates = tickers.length > 0;

        if (hasTickerUpdates) {
          logger.info(`Polling found ${tickers.length} tickers with updates since ${this.lastKnownUpdateTime}`);
          this.lastKnownUpdateTime = effectiveUpdateTime || this.lastKnownUpdateTime;
          this.consecutiveEmptyPolls = 0;
        } else {
          this.consecutiveEmptyPolls++;
          logger.debug(
            `Polling found no new updates (${this.consecutiveEmptyPolls} consecutive empty polls)`
          );
        }

        this.lastPollTime = pollTimestamp;

        const latestUpdateTimestamp = effectiveUpdateTime
          ? effectiveUpdateTime.toISOString()
          : null;

        // Always notify subscribers so the UI can refresh after each polling cycle
        this.notifySubscribers({
          updated_tickers: tickers,
          summary,
          market_closed: marketClosed,
          market_info: marketInfo,
          count: tickers.length,
          time: pollTimestamp.toISOString(),
          latest_update_time: latestUpdateTimestamp || responseData.latest_update_time || null,
          source: 'polling',
          message: hasTickerUpdates
            ? `Polling found ${tickers.length} updated tickers`
            : 'Polling cycle completed with no new ticker changes',
          has_updates: hasTickerUpdates,
          pollingCycleComplete: true,
          consecutive_empty_polls: this.consecutiveEmptyPolls
        });
      }
    } catch (error) {
      logger.error('Error during ticker polling:', error);
      this.consecutiveEmptyPolls++;
    }
  }

  /**
   * Get the latest update time from tickers
   * @param {Array} tickers - Array of ticker objects
   * @returns {Date|null} Latest update time or null
   */
  getLatestUpdateTime(tickers) {
    if (!tickers || tickers.length === 0) {
      return null;
    }

    let latestTime = null;
    tickers.forEach(ticker => {
      if (ticker.last_updated) {
        const updateTime = new Date(ticker.last_updated);
        if (!latestTime || updateTime > latestTime) {
          latestTime = updateTime;
        }
      }
    });

    return latestTime;
  }

  /**
   * Check if there are new updates since last known update time
   * @param {Date} currentUpdateTime - Current latest update time
   * @returns {boolean} True if there are new updates
   */
  hasNewUpdates(currentUpdateTime) {
    if (!currentUpdateTime) {
      return false;
    }

    if (!this.lastKnownUpdateTime) {
      // First poll - consider it as having updates
      return true;
    }

    return currentUpdateTime > this.lastKnownUpdateTime;
  }

  /**
   * Enable or disable polling
   * @param {boolean} enabled - Whether polling should be enabled
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    if (!enabled && this.isPolling) {
      this.stopPolling();
    }
  }

  /**
   * Set market closed status
   * @param {boolean} closed - Whether the market is closed
   */
  setMarketClosed(closed) {
    this.marketClosed = closed;
    logger.info(`Market status updated: ${closed ? 'closed' : 'open'}`);
  }

  /**
   * Get current polling status
   * @returns {Object} Polling status information
   */
  getStatus() {
    return {
      isPolling: this.isPolling,
      isEnabled: this.isEnabled,
      marketClosed: this.marketClosed,
      pollingIntervalMs: this.pollingIntervalMs,
      lastPollTime: this.lastPollTime ? this.lastPollTime.toISOString() : null,
      lastKnownUpdateTime: this.lastKnownUpdateTime ? this.lastKnownUpdateTime.toISOString() : null,
      consecutiveEmptyPolls: this.consecutiveEmptyPolls,
      subscriberCount: this.subscribers.size
    };
  }

  /**
   * Force a poll immediately
   */
  async forcePoll() {
    logger.info('Forcing immediate ticker poll...');
    await this.poll();
  }

  /**
   * Set the initial known update time (useful for initialization)
   * @param {Date} updateTime - The last known update time
   */
  setInitialUpdateTime(updateTime) {
    this.lastKnownUpdateTime = updateTime;
    logger.debug('Set initial update time:', updateTime);
  }

  loadStoredInterval() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }

      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return null;
      }

      return Math.max(1000, Math.floor(parsed));
    } catch (error) {
      logger.warn('Failed to load polling interval from storage:', error);
      return null;
    }
  }

  saveStoredInterval(intervalMs) {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, String(Math.max(1000, Math.floor(intervalMs || 0))));
    } catch (error) {
      logger.warn('Failed to persist polling interval to storage:', error);
    }
  }

  getCurrentInterval() {
    return this.pollingIntervalMs;
  }
}

// Create singleton instance
const tickerPollingService = new TickerPollingService();

export default tickerPollingService;
export { TickerPollingService };
